import { EventImpl } from '@fullcalendar/core/internal';
import { CalendarTaskSync } from '@griply/types';
import React from 'react';
import { getCalendarEventDate } from 'shared/lib/full-calendar';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { SmartList } from 'shared/types/smart-list';
import { Tag } from 'shared/types/tag';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';

import { ExternalEvent } from './external-event';
import { GoalEvent } from './goal-event';
import { LifeAreaEvent } from './life-area-event';
import { TaskEvent } from './task-event';

export type EventViewProps = {
  event: Pick<EventImpl, 'extendedProps' | 'start' | 'end' | 'allDay'>;
  timeText?: string;
  isPast?: boolean;
  tasks: Task[];
  goals: Goal[];
  lifeAreas: LifeArea[];
  tags: Tag[];
  smartLists: SmartList[];
  onComplete: (id: ID, date: Timestamp) => void;
  onClick?: (id: ID) => void;
  onInteractionComplete?: () => void;
  calendarSync?: CalendarTaskSync;
};

const arePropsEqual = (
  prevProps: EventViewProps,
  nextProps: EventViewProps,
): boolean => {
  // Check event.start?.toISOString() specifically
  if (
    prevProps.event.start?.toISOString() !==
    nextProps.event.start?.toISOString()
  ) {
    return false;
  }

  if (prevProps.timeText !== nextProps.timeText) {
    return false;
  }

  if (prevProps.isPast !== nextProps.isPast) {
    return false;
  }

  // Check extendedProps specifically
  const prevExtProps = prevProps.event.extendedProps;
  const nextExtProps = nextProps.event.extendedProps;
  if (
    prevExtProps.taskId !== nextExtProps.taskId ||
    prevExtProps.goalId !== nextExtProps.goalId ||
    prevExtProps.isDeadline !== nextExtProps.isDeadline ||
    prevExtProps.showDeadlineFlag !== nextExtProps.showDeadlineFlag ||
    prevExtProps.lifeAreaId !== nextExtProps.lifeAreaId ||
    prevExtProps.externalId !== nextExtProps.externalId ||
    prevExtProps.occurrenceId !== nextExtProps.occurrenceId ||
    prevExtProps.completed !== nextExtProps.completed ||
    // completed stays constant across e.g. Checked -> RepeatChecked, so it cannot stand in for these
    prevExtProps.checkOption !== nextExtProps.checkOption ||
    prevExtProps.isFutureRepeating !== nextExtProps.isFutureRepeating
  ) {
    return false;
  }

  // Check other props with shallow equality
  return !(
    prevProps.tasks !== nextProps.tasks ||
    prevProps.goals !== nextProps.goals ||
    prevProps.lifeAreas !== nextProps.lifeAreas ||
    prevProps.tags !== nextProps.tags ||
    prevProps.smartLists !== nextProps.smartLists ||
    prevProps.onComplete !== nextProps.onComplete ||
    prevProps.onClick !== nextProps.onClick ||
    prevProps.onInteractionComplete !== nextProps.onInteractionComplete ||
    prevProps.calendarSync !== nextProps.calendarSync
  );
};

const FullCalendarEventComponent: React.FC<EventViewProps> = ({
  event,
  timeText,
  isPast,
  tasks,
  goals,
  lifeAreas,
  tags,
  smartLists,
  onComplete,
  onClick,
  onInteractionComplete,
  calendarSync,
}) => {
  const startDate = getCalendarEventDate(event.start, event.allDay);
  const endDate = getCalendarEventDate(event.end, event.allDay);

  if (event.extendedProps.taskId) {
    return (
      <TaskEvent
        taskId={event.extendedProps.taskId}
        weekStartsOn={event.extendedProps.weekStartsOn}
        isDeadline={event.extendedProps.isDeadline}
        showDeadlineFlag={event.extendedProps.showDeadlineFlag}
        occurrenceId={event.extendedProps.occurrenceId}
        startDate={startDate}
        endDate={endDate}
        timeText={timeText}
        completed={event.extendedProps.completed}
        checkOption={event.extendedProps.checkOption}
        isFutureRepeating={event.extendedProps.isFutureRepeating}
        tasks={tasks}
        goals={goals}
        lifeAreas={lifeAreas}
        tags={tags}
        smartLists={smartLists}
        onComplete={onComplete}
        onClick={onClick}
        onInteractionComplete={onInteractionComplete}
        fallbackTask={event.extendedProps.task}
        calendarSync={calendarSync}
      />
    );
  }

  if (event.extendedProps.goalId) {
    return (
      <GoalEvent
        goalId={event.extendedProps.goalId}
        startDate={startDate}
        endDate={endDate}
        isDeadline={event.extendedProps.isDeadline}
        goals={goals}
        lifeAreas={lifeAreas}
        onClick={onClick}
      />
    );
  }

  if (event.extendedProps.lifeAreaId) {
    return (
      <LifeAreaEvent
        lifeAreaId={event.extendedProps.lifeAreaId}
        startDate={startDate}
        endDate={endDate}
        isPast={isPast}
        lifeAreas={lifeAreas}
        onClick={onClick}
      />
    );
  }

  if (event.extendedProps.externalId) {
    return (
      <ExternalEvent
        externalId={event.extendedProps.externalId}
        name={event.extendedProps.name}
        url={event.extendedProps.url}
        color={event.extendedProps.color}
        connectionId={event.extendedProps.connectionId}
        calendarId={event.extendedProps.calendarId}
        isRecurring={event.extendedProps.isRecurring}
        allDay={event.allDay}
        startDate={startDate}
        endDate={endDate}
        timeText={timeText}
        isPast={isPast}
      />
    );
  }

  return null;
};

export const FullCalendarEvent = React.memo(
  FullCalendarEventComponent,
  arePropsEqual,
);
